/**
 * Form controls (design-system.md §14). Each control owns its label, help text and error,
 * wired with `aria-describedby` / `aria-invalid`; the required marker is the spec's
 * visual `*` (aria-hidden) plus an sr-only "(required)" placed BESIDE the label (not inside
 * it, so the control's accessible name is exactly the label text), and the input itself
 * carries both `required` and `aria-required`.
 */
import { AlertCircle, Eye, EyeOff } from './icons.ts';
import {
  forwardRef,
  useId,
  useState,
  type InputHTMLAttributes,
  type ReactNode,
  type SelectHTMLAttributes,
  type TextareaHTMLAttributes,
} from 'react';

import { Button } from './button.tsx';

/**
 * The password reveal toggle's accessible names (F-30). Shared with apps/web's auth-shell
 * `PasswordField` (its components/ui.tsx imports them from here), so both reveals read the same.
 */
export const SHOW_PASSWORD_LABEL = 'Show password';
export const HIDE_PASSWORD_LABEL = 'Hide password';

export interface FieldChromeProps {
  label: ReactNode;
  /** Renders the §14 required marker (and sets `required` + `aria-required`). */
  required?: boolean | undefined;
  /** Renders "(optional)" after the label. */
  optional?: boolean | undefined;
  help?: ReactNode | undefined;
  error?: ReactNode | undefined;
  /** Visually hide the label (it stays in the accessibility tree). */
  hideLabel?: boolean | undefined;
}

interface FieldFrameProps extends FieldChromeProps {
  id: string;
  children: ReactNode;
  className?: string | undefined;
}

function describedBy(id: string, help: ReactNode, error: ReactNode): string | undefined {
  const ids = [error ? `${id}-error` : '', help ? `${id}-help` : ''].filter(Boolean);
  return ids.length > 0 ? ids.join(' ') : undefined;
}

/** The label / control / help / error frame. Exported for composite controls. */
export function FieldFrame({
  id,
  label,
  required,
  optional,
  help,
  error,
  hideLabel,
  children,
  className,
}: FieldFrameProps): JSX.Element {
  return (
    <div className={['argus-field', className ?? ''].filter(Boolean).join(' ')}>
      <div className={hideLabel ? 'sr-only' : 'argus-field__label-row'}>
        <label htmlFor={id} className="argus-field__label">
          {label}
        </label>
        {required ? (
          <>
            <span className="argus-field__required" aria-hidden="true">
              *
            </span>
            <span className="sr-only">(required)</span>
          </>
        ) : null}
        {optional && !required ? <span className="argus-field__optional">(optional)</span> : null}
      </div>
      {children}
      {error ? (
        <p id={`${id}-error`} className="argus-field__error" role="alert">
          <AlertCircle size={14} aria-hidden="true" />
          <span>{error}</span>
        </p>
      ) : null}
      {help ? (
        <p id={`${id}-help`} className="argus-field__help">
          {help}
        </p>
      ) : null}
    </div>
  );
}

export interface InputProps
  extends Omit<InputHTMLAttributes<HTMLInputElement>, 'required'>,
    FieldChromeProps {
  mono?: boolean | undefined;
  fieldSize?: 'sm' | 'md' | undefined;
  /**
   * F-30: with `type="password"`, adds the in-field eye toggle (the in-app twin of the auth
   * `PasswordField` reveal — same labels). The toggle is a sibling of the input, OUTSIDE the
   * label, so it never leaks into the field's accessible name; it is `aria-pressed` and
   * `aria-controls` the input. Ignored for any other `type`.
   */
  reveal?: boolean | undefined;
}

export const Input = forwardRef<HTMLInputElement, InputProps>(function Input(
  {
    id,
    label,
    required,
    optional,
    help,
    error,
    hideLabel,
    mono,
    fieldSize,
    reveal,
    type,
    className,
    ...rest
  },
  ref,
) {
  const autoId = useId();
  const inputId = id ?? autoId;
  const [revealed, setRevealed] = useState(false);
  const revealable = reveal === true && type === 'password';
  const input = (
    <input
      ref={ref}
      id={inputId}
      type={revealable && revealed ? 'text' : type}
      className={[
        'argus-input',
        mono ? 'argus-input--mono' : '',
        fieldSize === 'sm' ? 'argus-input--sm' : '',
      ]
        .filter(Boolean)
        .join(' ')}
      required={required}
      aria-required={required || undefined}
      aria-invalid={error ? true : undefined}
      aria-describedby={describedBy(inputId, help, error)}
      {...rest}
    />
  );
  return (
    <FieldFrame
      id={inputId}
      label={label}
      required={required}
      optional={optional}
      help={help}
      error={error}
      hideLabel={hideLabel}
      className={className}
    >
      {revealable ? (
        <div className="argus-input-wrap">
          {input}
          <Button
            variant="ghost"
            size="sm"
            iconOnly
            className="argus-input__reveal"
            aria-label={revealed ? HIDE_PASSWORD_LABEL : SHOW_PASSWORD_LABEL}
            aria-pressed={revealed}
            aria-controls={inputId}
            onClick={() => setRevealed((r) => !r)}
            icon={
              revealed ? (
                <EyeOff size={16} aria-hidden="true" />
              ) : (
                <Eye size={16} aria-hidden="true" />
              )
            }
          />
        </div>
      ) : (
        input
      )}
    </FieldFrame>
  );
});

export interface SelectProps
  extends Omit<SelectHTMLAttributes<HTMLSelectElement>, 'required'>,
    FieldChromeProps {
  fieldSize?: 'sm' | 'md' | undefined;
}

export const Select = forwardRef<HTMLSelectElement, SelectProps>(function Select(
  {
    id,
    label,
    required,
    optional,
    help,
    error,
    hideLabel,
    fieldSize,
    className,
    children,
    ...rest
  },
  ref,
) {
  const autoId = useId();
  const selectId = id ?? autoId;
  return (
    <FieldFrame
      id={selectId}
      label={label}
      required={required}
      optional={optional}
      help={help}
      error={error}
      hideLabel={hideLabel}
      className={className}
    >
      <select
        ref={ref}
        id={selectId}
        className={['argus-select', fieldSize === 'sm' ? 'argus-select--sm' : '']
          .filter(Boolean)
          .join(' ')}
        required={required}
        aria-required={required || undefined}
        aria-invalid={error ? true : undefined}
        aria-describedby={describedBy(selectId, help, error)}
        {...rest}
      >
        {children}
      </select>
    </FieldFrame>
  );
});

export interface TextareaProps
  extends Omit<TextareaHTMLAttributes<HTMLTextAreaElement>, 'required'>,
    FieldChromeProps {}

export const Textarea = forwardRef<HTMLTextAreaElement, TextareaProps>(function Textarea(
  { id, label, required, optional, help, error, hideLabel, className, ...rest },
  ref,
) {
  const autoId = useId();
  const areaId = id ?? autoId;
  return (
    <FieldFrame
      id={areaId}
      label={label}
      required={required}
      optional={optional}
      help={help}
      error={error}
      hideLabel={hideLabel}
      className={className}
    >
      <textarea
        ref={ref}
        id={areaId}
        className="argus-textarea"
        required={required}
        aria-required={required || undefined}
        aria-invalid={error ? true : undefined}
        aria-describedby={describedBy(areaId, help, error)}
        {...rest}
      />
    </FieldFrame>
  );
});

export interface CheckboxProps extends Omit<InputHTMLAttributes<HTMLInputElement>, 'type'> {
  label: ReactNode;
  description?: ReactNode | undefined;
}

export const Checkbox = forwardRef<HTMLInputElement, CheckboxProps>(function Checkbox(
  { label, description, className, ...rest },
  ref,
) {
  return (
    <label className={['argus-check', className ?? ''].filter(Boolean).join(' ')}>
      <input ref={ref} type="checkbox" {...rest} />
      <span>
        {label}
        {description ? <span className="argus-field__help">{description}</span> : null}
      </span>
    </label>
  );
});
